/**
 * The step bar (29 Oct, Rusty: "map out the process … like the status").
 *
 * A case's road (New: Requested → Account created → Roles chosen → Approved
 * → Access granted → Done; Change and Leaver their own) and each item's own,
 * from `journey` / `steps` (the server's `access_journey`): done steps say who
 * and when, the current one who it waits on, a rejection who rejected it, and
 * steps an item does not have ("not needed") or never reached are dashed.
 *
 * `variant`: "full" on the case page, "mini" on each item ticket, "dots" in
 * the Access list.
 */

import { CircleCheck, CircleX, Hourglass, Icon } from "@norican/icons";
import { cn, Typography } from "@norican/react";

import { useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type { AccessJourneyStep } from "@/lib/api";

export interface JourneyBarProps {
  steps: AccessJourneyStep[];
  variant?: "full" | "mini" | "dots";
  /** Item steps: "form" reads "Form completed", not the case's "Roles chosen". */
  item?: boolean;
  className?: string;
}

export function JourneyBar({ steps, variant = "full", item = false, className }: JourneyBarProps) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  if (steps.length === 0) {
    return null;
  }

  function label(step: AccessJourneyStep): string {
    if (step.state === "rejected") {
      return t("access.journey.rejected"); // 29 Oct: not "Approved (Rejected by …)"
    }
    return item && step.key === "form"
      ? t("access.journey.itemForm")
      : t(`access.journey.step.${step.key}`);
  }

  function caption(step: AccessJourneyStep): string | null {
    const date = step.at ? formatDate(new Date(step.at)) : null;
    switch (step.state) {
      case "done":
        return [step.who, date].filter(Boolean).join(", ") || null;
      case "current":
        return step.who
          ? t("access.journey.waitingOn", { who: step.who })
          : t("access.journey.inProgress");
      case "rejected":
        return step.who ?? null;
      case "skipped":
        return step.key === "approval" ? t("access.journey.notNeeded") : null;
      default:
        return null;
    }
  }

  if (variant === "dots") {
    const current = steps.find((step) => step.state === "current" || step.state === "rejected");
    const last = [...steps].reverse().find((step) => step.state === "done");
    const shown = current ?? last;
    return (
      <span
        className={cn("inline-flex min-w-0 items-center gap-2", className)}
        aria-label={t("access.journey.label")}
      >
        <span className="inline-flex items-center gap-1" aria-hidden>
          {steps.map((step) => (
            <Dot key={step.key} state={step.state} />
          ))}
        </span>
        {shown ? (
          <Typography as="span" variant="caption" className="min-w-0 truncate">
            {label(shown)}
          </Typography>
        ) : null}
      </span>
    );
  }

  const mini = variant === "mini";
  return (
    <ol
      aria-label={t("access.journey.label")}
      className={cn(
        "m-0 flex list-none p-0",
        mini ? "flex-wrap items-start gap-x-1 gap-y-1" : "flex-col gap-3 sm:flex-row sm:gap-0",
        className,
      )}
    >
      {steps.map((step, index) => {
        const words = caption(step);
        return (
          <li
            key={step.key}
            aria-current={step.state === "current" ? "step" : undefined}
            className={cn(
              "flex min-w-0",
              mini ? "items-center gap-1" : "items-start gap-2 sm:flex-1 sm:flex-col sm:gap-1",
            )}
          >
            {mini && index > 0 ? (
              <span aria-hidden className="text-fg-muted text-xs">
                →
              </span>
            ) : null}
            <span className={cn("flex items-center", mini ? "gap-1" : "w-full gap-2")}>
              <Marker state={step.state} size={mini ? "xs" : "sm"} />
              {!mini && index < steps.length - 1 ? (
                <span
                  aria-hidden
                  className={cn(
                    "hidden h-px flex-1 sm:block",
                    step.state === "done" ? "bg-success-fg" : "bg-border",
                  )}
                />
              ) : null}
            </span>
            <span className={cn("flex min-w-0 flex-col", mini ? "" : "sm:pr-2")}>
              <Typography
                as="span"
                variant={mini ? "caption" : "body-sm"}
                className={cn(
                  step.state === "current" && "font-semibold",
                  step.state === "rejected" && "text-danger-fg font-semibold",
                  (step.state === "todo" || step.state === "skipped") && "text-fg-muted",
                  step.state === "skipped" && "line-through decoration-1",
                )}
              >
                {label(step)}
                {mini && words ? (
                  <span className="text-fg-muted font-normal no-underline">{` (${words})`}</span>
                ) : null}
              </Typography>
              {!mini && words ? (
                <Typography as="span" variant="caption" className="text-fg-muted break-words">
                  {words}
                </Typography>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Marker({ state, size }: { state: AccessJourneyStep["state"]; size: "xs" | "sm" }) {
  if (state === "done") {
    return <Icon icon={CircleCheck} size={size} tone="success" />;
  }
  if (state === "rejected") {
    return <Icon icon={CircleX} size={size} tone="danger" />;
  }
  if (state === "current") {
    return <Icon icon={Hourglass} size={size} tone="accent" />;
  }
  return (
    <span
      aria-hidden
      className={cn(
        "border-border inline-block shrink-0 rounded-full border-2",
        size === "xs" ? "size-3" : "size-4",
        state === "skipped" && "border-dashed",
      )}
    />
  );
}

function Dot({ state }: { state: AccessJourneyStep["state"] }) {
  return (
    <span
      className={cn(
        "inline-block size-2 rounded-full",
        state === "done" && "bg-success-fg",
        state === "current" && "bg-accent-fg",
        state === "rejected" && "bg-danger-fg",
        state === "todo" && "border-border border",
        state === "skipped" && "border-border border border-dashed",
      )}
    />
  );
}
